import React, { useState } from 'react';
import { Button } from './ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { RadiantStar } from './ui/RadiantStar';
const fullStory = ["Throughout my career, I often felt the pressure to keep moving forward. To build a successful career, meet expectations, and become the person I thought I was supposed to be. Looking back, many of those expectations didn't come from me. They came from society, the people around me, and the image I believed I had to live up to.", 'Over time, I discovered that work and life are deeply connected. The more balanced I felt outside of work, the more confident I became in the choices I made within it. Friends, family, nature, and my dog Django became an important foundation during that journey.', "I've found answers in many places, from silent retreats, breathing exercises, cold water, meditation and long walks in nature. But the greatest clarity I've ever gained came through coaching. Not because someone gave me the answers, but because coaching helped me slow down and ask myself the right questions.", 'That is exactly what I hope to help others experience.'];
export function HeroSection() {
  const [storyOpen, setStoryOpen] = useState(false);
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-28 pb-16" data-id="element-58" data-mp="2p7em4310eoam">
      {/* Background with gradient and texture */}
      <div className="absolute inset-0 bg-gradient-to-b from-night-900 via-night-800 to-earth-900 z-0" data-id="element-59" data-mp="2z2gbmzz75sdq"></div>
      <div className="absolute inset-0 bg-grain opacity-20 z-0 mix-blend-overlay" data-id="element-60" data-mp="2u3mv0yom5l9c"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 w-full" data-id="element-61" data-mp="3834ovsgrnopk">
        {/* Top — centered star + headline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        ease: 'easeOut'
      }} className="text-center mb-12" data-id="element-62" data-mp="335mhi6iedhjo">
          <div className="flex justify-center mb-5" data-id="element-63" data-mp="18swmtmk7g4b8">
            <RadiantStar size={64} animate={true} data-id="element-64" data-mp="38u4kl2ee1eav" />
          </div>

          <motion.h1 initial={{
          opacity: 0,
          filter: 'blur(4px)'
        }} animate={{
          opacity: 1,
          filter: 'blur(0px)'
        }} transition={{
          duration: 1.2,
          delay: 0.3,
          ease: 'easeOut'
        }} className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-gold-400 mb-2 text-glow" data-id="element-65" data-mp="3dsefdbw7acvy">
            Feeling overwhelmed?
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.7,
          ease: 'easeOut'
        }} className="font-serif text-lg md:text-2xl leading-snug text-cream-50 font-light max-w-2xl mx-auto" data-id="element-66" data-mp="3rqcr09j4zv7r">
            From stuck in expectations to living a life that finally feels like
            yours.
          </motion.p>
        </motion.div>

        {/* Bottom — moodboard left, story right */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-start mb-10" data-id="element-67" data-mp="jaaz678bzmhm">
          {/* Left — moodboard */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 1,
          delay: 0.4,
          ease: 'easeOut'
        }} className="flex justify-center md:justify-start" data-id="element-68" data-mp="1yvbj5sgmxmia">
            <div className="relative" data-id="element-69" data-mp="1pvyf5fba5q4y">
              <div className="absolute -inset-2 bg-gold-400/15 rounded-2xl transform rotate-2" data-id="element-70" data-mp="2mkwe79q2ca7x"></div>
              <img src="https://cdn.magicpatterns.com/uploads/sjqk9147GGzZcR6Q2opMuU/ChatGPT_Image_26_mei_2026,_20_30_35.png" alt="Mark Vermeulen" className="relative rounded-2xl shadow-2xl w-full max-w-[460px] object-contain border border-gold-400/20" data-id="element-71" data-mp="2ijasqzzgqnm4" />
            </div>
          </motion.div>

          {/* Right — Your Coach story with hover reveal */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 1,
          delay: 0.6,
          ease: 'easeOut'
        }} onMouseEnter={() => setStoryOpen(true)} onMouseLeave={() => setStoryOpen(false)} data-id="element-72" data-mp="31hn48sduk9kv">
            <div tabIndex={0} onFocus={() => setStoryOpen(true)} onBlur={() => setStoryOpen(false)} onClick={() => setStoryOpen(open => !open)} className="cursor-pointer rounded-2xl border border-gold-400/20 bg-night-900/40 backdrop-blur-sm p-6 md:p-7 transition-colors duration-300 hover:border-gold-400/45 focus:outline-none focus:border-gold-400/60" data-id="element-73" data-mp="2c294bybj2ra6">
              <h2 className="font-serif text-2xl md:text-3xl text-gold-400 mb-4" data-id="element-74" data-mp="2v0bwun3ab6cq">
                Your Coach
              </h2>

              <p className="font-serif italic text-cream-50/60 text-base leading-relaxed" data-id="element-75" data-mp="2zxlidrnjyxva">
                "Choose a degree with job security." "Work for a large company."
                "Build a successful career." "Climb the corporate ladder." "Buy
                a house."
              </p>
              <p className="text-cream-50/90 text-base leading-relaxed mt-5" data-id="element-76" data-mp="5nk84dxefsra">
                For years, I believed that success meant following the path
                society had laid out for me. Coaching taught me that success
                isn't something you achieve; it's something you define by asking
                yourself the right questions.
              </p>
              <p className="text-cream-50/90 text-base leading-relaxed mt-4" data-id="element-77" data-mp="3iwoyl2rua14h">
                That shift changed my life and inspired me to help others create
                a direction that truly feels like their own.
              </p>

              {/* Expanded story */}
              <AnimatePresence initial={false} data-id="element-78" data-mp="3nu8dm4wd2hxh">
                {storyOpen && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.45,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-79" data-mp="183m47dfjv4da">
                    <div className="pt-5 mt-5 border-t border-gold-400/20 space-y-4" data-id="element-80" data-mp="1f5r1f4vzbhq7">
                      {fullStory.map((paragraph, index) => <p key={index} className="text-cream-50/80 text-sm leading-relaxed" data-id="element-81" data-mp="1p59i9cglene1" data-mpi={index}>
                          {paragraph}
                        </p>)}

                      <p className="font-serif text-cream-50 text-base pt-1" data-id="element-82" data-mp="wh40myku5eif">
                        Recognizable?{' '}
                        <a href="#about" className="text-gold-400 hover:text-gold-300 underline underline-offset-2 transition-colors font-sans text-sm" data-id="element-83" data-mp="166nfzu9caq8j">
                          (click here)
                        </a>
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {!storyOpen && <span className="mt-5 inline-block text-xs uppercase tracking-widest text-gold-400/70" data-id="element-84" data-mp="18a40a957wea">
                  Hover to read my story
                </span>}
            </div>
          </motion.div>
        </div>

        {/* Buttons — centered below */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.2,
        duration: 0.8
      }} className="relative z-20 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5" data-id="element-85" data-mp="3jef0ja0fn7il">
          <Button variant="primary" href="#contact" data-id="element-86" data-mp="34opje5by2swo">
            Book a Session
          </Button>
          <Button variant="outline" href="#offerings" data-id="element-87" data-mp="2zp4me0gc4uml">
            Discover Group Journeys
          </Button>
        </motion.div>
      </div>

      {/* Bottom fade to blend with next section */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-earth-900 to-transparent z-10 pointer-events-none" data-id="element-88" data-mp="nf314n5f1079"></div>
    </section>;
}